// ============================================================
// TMR Portal Admin — aba Histórico dentro do detalhe da empresa (07)
// ============================================================
import { useState } from 'react';
import { Btn } from '../../components/primitives';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { useCompanyActivity } from '../../data/activity/queries';
import {
  CATEGORY_LABEL,
  CATEGORY_ORDER,
  activityActor,
  activityInstant,
  activityPhrase,
} from '../../data/activity/messages';
import type { ActivityCategory, ActivityCounts, ActivityEntry } from '../../data/activity/contract';

/** Props for {@link CompanyActivityTab}. */
interface CompanyActivityTabProps {
  companyId: string;
}

/** The tab being read; `all` is "Tudo" — every category the operator may see. */
type Tab = 'all' | ActivityCategory;

/**
 * What happened to this Empresa: the registration, the contracts, the money, the rates and what the
 * platform did on its own — one list, newest first (`07`).
 *
 * Three things about this screen are not cosmetic:
 *
 * - **Every line names a responsible party.** A history that says something changed without saying who is
 *   the history nobody consults during the incident it was written for. `Sistema` is a real answer, and a
 *   different one from a missing name.
 * - **The wording is composed here, from a stable key.** The backend stores `contract.signed` plus
 *   structured references and never a sentence (`07` §5), so the phrases in `messages.ts` can be reworded
 *   without touching a single stored row — and a key this build does not know still renders.
 * - **A tab the operator cannot read is absent, not empty.** The backend returns a null count for those,
 *   which is a different statement from zero: showing "Taxas (0)" to somebody without `company.rates.read`
 *   would tell them there is nothing there, which is not what the server said.
 */
export function CompanyActivityTab({ companyId }: CompanyActivityTabProps) {
  const [tab, setTab] = useState<Tab>('all');
  const query = useCompanyActivity(companyId, tab === 'all' ? undefined : tab);

  const pages = query.data?.pages ?? [];
  const entries = pages.flatMap((page) => page.items);
  // Every page carries the counts, and they are the same each time — the first one is as good as any, and
  // reading them from the newest page keeps the badges in step with a refetch.
  const counts = pages[0]?.counts;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {counts ? <Tabs active={tab} counts={counts} onChange={setTab} /> : null}

      {query.isPending ? (
        <Skeleton rows={5} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : entries.length === 0 ? (
        <EmptyState
          message={
            tab === 'all'
              ? 'Nada registrado para esta empresa ainda.'
              : `Nada registrado em ${CATEGORY_LABEL[tab]}.`
          }
          // The timeline starts when this version ships and is never backfilled from the legacy (`07` §2),
          // so an Empresa migrated last year has a real history that simply was not recorded. Saying so
          // beats implying nothing ever happened to it.
          hint="O histórico passou a ser gravado a partir desta versão — ações anteriores não têm linha aqui."
        />
      ) : (
        <>
          <ol style={{ display: 'grid', gap: 2, listStyle: 'none', margin: 0, padding: 0 }}>
            {entries.map((entry) => (
              <Entry key={entry.id} entry={entry} />
            ))}
          </ol>

          {query.hasNextPage ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Btn
                variant="ghost"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </Btn>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Props for {@link Tabs}. */
interface TabsProps {
  active: Tab;
  counts: ActivityCounts;
  onChange: (tab: Tab) => void;
}

/**
 * The category filter, with the entry count of each tab.
 *
 * A category the server reported as null is not rendered at all — see the note on the component above.
 */
function Tabs({ active, counts, onChange }: TabsProps) {
  const available = CATEGORY_ORDER.filter((category) => countOf(counts, category) !== null);

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <TabButton label="Tudo" count={counts.all} active={active === 'all'} onClick={() => onChange('all')} />
      {available.map((category) => (
        <TabButton
          key={category}
          label={CATEGORY_LABEL[category]}
          count={countOf(counts, category) ?? 0}
          active={active === category}
          onClick={() => onChange(category)}
        />
      ))}
    </div>
  );
}

/** Props for {@link TabButton}. */
interface TabButtonProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

function TabButton({ label, count, active, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 'var(--radius-pill, 999px)',
        border: '1px solid var(--border-default)',
        background: active ? 'var(--bg-selected, var(--bg-subtle))' : 'transparent',
        color: active ? 'var(--fg-default)' : 'var(--fg-muted)',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
      }}
    >
      {label}
      <span style={{ fontSize: 12, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}>
        {count}
      </span>
    </button>
  );
}

/** Props for {@link Entry}. */
interface EntryProps {
  entry: ActivityEntry;
}

/** One line of the timeline: what happened, who did it, and when. */
function Entry({ entry }: EntryProps) {
  return (
    <li
      style={{
        display: 'grid',
        gap: 4,
        padding: '12px 14px',
        borderBottom: '1px solid var(--border-subtle, var(--border-default))',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13.5, fontWeight: 550 }}>{activityPhrase(entry)}</span>
        <span style={{ fontSize: 12, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}>
          {activityInstant(entry.occurredAt)}
        </span>
      </div>
      <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>{activityActor(entry)}</span>
    </li>
  );
}

/** The count the server reported for a category — null when the operator may not read it. */
function countOf(counts: ActivityCounts, category: ActivityCategory): number | null {
  switch (category) {
    case 'Registration':
      return counts.registration;
    case 'Contract':
      return counts.contract;
    case 'Finance':
      return counts.finance;
    case 'Rates':
      return counts.rates;
    case 'BusinessRule':
      return counts.businessRule;
    case 'PatientContract':
      return counts.patientContract;
    case 'Credit':
      return counts.credit;
    case 'Contingency':
      return counts.contingency;
    case 'System':
      return counts.system;
  }
}
